import {useMemo} from 'react';

import {
  RegressionStepDataName,
  SINGLE_CHART_OUTPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {GetAnalysisDataFrame, GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {
  getIconAndTextForChartViewStep,
  getIconAndTextForRegressionStep,
} from 'analysis-tool/charting/AnalysisChartingUtils';
import getOutputStepDataForDataFrame from 'analysis-tool/data/getOutputStepDataForDataFrame';
import getLegacyStepFromAnalysis from 'analysis-tool/data/getStepFromAnalysis';
import {AnalysisMainViewItemType} from 'analysis-tool/ui/main-view/MainViewTypes';
import {getDatasetSectionSidebarItemDatas, SidebarItemData} from 'analysis-tool/ui/sidebar/SidebarItemDatas';
import {assert, assertAndContinue} from 'Asserts';
import {ResultsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {PipelineStepStatusCode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {getParentSteps, getTopLevelTableStartStepForViewLevelStep} from 'pipelines/StepLineageUtils';

import {isSendToDocumentEnabled} from './OutputsUtil';
import {
  AnalysisOutputConfigWithoutResultsMapping,
  AnalysisOutputOptionItem,
  AnalysisOutputType,
  OUTPUT_NAME_SEPARATOR,
} from './Types';

function isResultOptionEnabled(): boolean {
  return isFeatureEnabled({feature: ResultsFeature.RESULTS_CREATE});
}

function getResultOptionIfEnabled(
  baseConfig: Pick<AnalysisOutputConfigWithoutResultsMapping, 'outputName' | 'parentOutputName' | 'step'>,
  dataFrame: GetAnalysisDataFrame
): Array<AnalysisOutputConfigWithoutResultsMapping> {
  return isResultOptionEnabled()
    ? [
        {
          ...baseConfig,
          dataFrame,
          type: AnalysisOutputType.RESULT as const,
          icon: 'data_frame',
          targetSpecificMappingState: null,
        },
      ]
    : [];
}

function flattenSidebarItemDatas(
  sidebarItems: Array<SidebarItemData>,
  pipeline: PipelineGraphQL,
  indentLevel: number,
  shouldEnableOutputFromStep: ((step: GetAnalysisPipelineStep) => string | null) | undefined
): Array<AnalysisOutputOptionItem> {
  return sidebarItems.flatMap((sidebarItem) =>
    [
      ...sidebarItemToAnalysisOutputOptions(sidebarItem, pipeline, indentLevel, shouldEnableOutputFromStep),
    ].concat(
      flattenSidebarItemDatas(
        sidebarItem.childSidebarItems ?? [],
        pipeline,
        indentLevel + 1,
        shouldEnableOutputFromStep
      )
    )
  );
}

function sidebarItemToAnalysisOutputOptions(
  item: SidebarItemData,
  pipeline: PipelineGraphQL,
  indentLevel: number,
  shouldEnableOutputFromStep: ((step: GetAnalysisPipelineStep) => string | null) | undefined
): Array<AnalysisOutputOptionItem> {
  const stepId = (item.mainViewItem as {stepId: string}).stepId;
  const step = getLegacyStepFromAnalysis(pipeline, stepId);
  const baseOptionWithNoOutputConfig: AnalysisOutputOptionItem = {
    indentLevel,
    sidebarItemData: item,
    selectorIndex: -1,
    outputConfigs: [],
    disabledReason: null,
  };

  if (
    [
      AnalysisMainViewItemType.FAILED,
      AnalysisMainViewItemType.RUNNING,
      // EDIT_CHART is sometimes shown when we have an "Unfinished view...", but this doesn't have outputs.
      AnalysisMainViewItemType.EDIT_CHART,
    ].includes(item.mainViewItem.type) ||
    /**
     * The step may be running, even if the view type is not `AnalysisMainViewItemType.RUNNING`.
     *
     * `AnalysisMainViewItemType.DATA_FRAME` is sometimes shown for tables that are running, so the user can
     * see a prior version of data beneath the loading overlay. See `getDatasetRunningSidebarItemData`.
     */
    step.statusCode !== PipelineStepStatusCode.SUCCEEDED
  ) {
    return [baseOptionWithNoOutputConfig];
  }

  const disabledReason = shouldEnableOutputFromStep != null ? shouldEnableOutputFromStep(step) : null;
  if (disabledReason != null) {
    return [{...baseOptionWithNoOutputConfig, disabledReason}];
  }

  switch (item.mainViewItem.type) {
    case AnalysisMainViewItemType.CHART: {
      if (step.statusCode !== PipelineStepStatusCode.SUCCEEDED) {
        return [baseOptionWithNoOutputConfig];
      }
      const chartStepData = step.outputStepDatas.find(
        ({name}) => name === SINGLE_CHART_OUTPUT_STEP_DATA_NAME
      );
      const chart = chartStepData?.charts[0];

      const dataFrameStepData = step.outputStepDatas.find(
        ({name}) => name === SINGLE_DATASET_OUTPUT_STEP_DATA_NAME
      );
      const dataFrame = dataFrameStepData?.dataFrames[0];

      assert(dataFrame != null, 'Data frame should not be null');

      const baseOutputConfig = {
        outputName: String(item.title),
        parentOutputName:
          getTopLevelTableStartStepForViewLevelStep(pipeline, step)?.outputStepDatas[0].dataFrames[0].name ??
          null,
        step,
      };

      return [
        {
          ...baseOptionWithNoOutputConfig,
          outputConfigs: [
            ...(chart != null && !isInaccessible(chart) && isSendToDocumentEnabled()
              ? [
                  {
                    ...baseOutputConfig,
                    type: AnalysisOutputType.CHART as const,
                    chart,
                    icon: getIconAndTextForChartViewStep(step).icon,
                  },
                ]
              : []),
            {
              ...baseOutputConfig,
              type: AnalysisOutputType.DATASET,
              dataFrame,
              icon: 'dataset',
            },
            ...getResultOptionIfEnabled(baseOutputConfig, dataFrame),
          ],
        },
      ];
    }
    case AnalysisMainViewItemType.DATA_FRAME: {
      const dataFrameId = item.mainViewItem.dataFrameId;
      const outputStepData = getOutputStepDataForDataFrame(step, dataFrameId)!;
      const optionDataFrame = outputStepData.dataFrames.find(
        (outputDataFrame) => outputDataFrame.id === dataFrameId
      );
      if (optionDataFrame == null) {
        return [{...baseOptionWithNoOutputConfig}];
      }

      const baseOutputConfig = {
        outputName: String(item.title),
        parentOutputName:
          getTopLevelTableStartStepForViewLevelStep(pipeline, step)?.outputStepDatas[0].dataFrames[0].name ??
          null,
      };
      return [
        {
          ...baseOptionWithNoOutputConfig,
          outputConfigs: [
            {
              ...baseOutputConfig,
              type: AnalysisOutputType.DATASET as const,
              dataFrame: optionDataFrame,
              step,
              icon: 'dataset' as const,
            },
            ...getResultOptionIfEnabled({...baseOutputConfig, step}, optionDataFrame),
          ],
        },
      ];
    }
    case AnalysisMainViewItemType.REGRESSION: {
      const optionDataFrame = step.inputStepDatas[0].dataFrames[0];
      const modelOutputDataFrame = step.outputStepDatas.find(
        (stepData) => stepData.name === RegressionStepDataName.MODEL_OUTPUT
      )!.dataFrames[0];
      const modelVariablesDataFrame = step.outputStepDatas.find(
        (stepData) => stepData.name === RegressionStepDataName.MODEL_VARIABLES
      )!.dataFrames[0];
      // We need the regression's parent step because we need the parent data frame to be an output data frame
      const [parentStep] = getParentSteps(pipeline, step);

      const baseOutputConfig = {
        dataFrame: optionDataFrame,
        step,
        outputName: String(item.title),
        parentOutputName:
          getTopLevelTableStartStepForViewLevelStep(pipeline, step)?.outputStepDatas[0].dataFrames[0].name ??
          null,
      };
      const modelOutputBaseOutputConfig = {
        dataFrame: modelOutputDataFrame,
        step,
        outputName: `${item.title}${OUTPUT_NAME_SEPARATOR}${RegressionStepDataName.MODEL_OUTPUT}`,
        parentOutputName:
          getTopLevelTableStartStepForViewLevelStep(pipeline, step)?.outputStepDatas[0].dataFrames[0].name ??
          null,
      };

      const modelVariableBaseOutputConfig = {
        dataFrame: modelVariablesDataFrame,
        step,
        outputName: `${item.title}${OUTPUT_NAME_SEPARATOR}${RegressionStepDataName.MODEL_VARIABLES}`,
        parentOutputName:
          getTopLevelTableStartStepForViewLevelStep(pipeline, step)?.outputStepDatas[0].dataFrames[0].name ??
          null,
      };

      const chartStepData = step.outputStepDatas.find(
        ({name}) => name === SINGLE_CHART_OUTPUT_STEP_DATA_NAME
      );
      const chart = chartStepData?.charts[0];

      return [
        {
          ...baseOptionWithNoOutputConfig,
          outputConfigs: [
            ...(chart != null && !isInaccessible(chart) && isSendToDocumentEnabled()
              ? [
                  {
                    ...baseOutputConfig,
                    type: AnalysisOutputType.CHART as const,
                    chart,
                    icon: getIconAndTextForRegressionStep(step).icon,
                  },
                ]
              : []),
            {
              ...baseOutputConfig,
              step: parentStep,
              type: AnalysisOutputType.DATASET as const,
              icon: 'dataset' as const,
            },
            ...getResultOptionIfEnabled({...baseOutputConfig, step: parentStep}, baseOutputConfig.dataFrame),
          ],
        },
        modelOutputDataFrame != null
          ? {
              ...baseOptionWithNoOutputConfig,
              outputConfigs: [
                {
                  ...modelOutputBaseOutputConfig,
                  type: AnalysisOutputType.DATASET as const,
                  icon: 'dataset' as const,
                },
                ...getResultOptionIfEnabled(
                  modelOutputBaseOutputConfig,
                  modelOutputBaseOutputConfig.dataFrame
                ),
              ],
            }
          : baseOptionWithNoOutputConfig,
        modelVariablesDataFrame != null
          ? {
              ...baseOptionWithNoOutputConfig,
              outputConfigs: [
                {
                  ...modelVariableBaseOutputConfig,
                  type: AnalysisOutputType.DATASET as const,
                  icon: 'dataset' as const,
                },
                ...getResultOptionIfEnabled(
                  modelVariableBaseOutputConfig,
                  modelVariableBaseOutputConfig.dataFrame
                ),
              ],
            }
          : baseOptionWithNoOutputConfig,
      ];
    }

    default:
      assertAndContinue(false, 'AnalysisOutputSelector received unexpected item type', {
        itemType: item.mainViewItem.type,
      });
      return [baseOptionWithNoOutputConfig];
  }
}

export default function usePossibleOutputOptions(
  pipeline: PipelineGraphQL,
  shouldEnableOutputFromStep: ((step: GetAnalysisPipelineStep) => string | null) | undefined
): Array<AnalysisOutputOptionItem> {
  return useMemo(
    () =>
      flattenSidebarItemDatas(
        getDatasetSectionSidebarItemDatas(pipeline),
        pipeline,
        0,
        shouldEnableOutputFromStep
      ).map((option, index) => ({
        ...option,
        selectorIndex: index,
      })),
    [pipeline, shouldEnableOutputFromStep]
  );
}
